/**
 * useMediaQuery — CSS 미디어쿼리를 JS 에서 구독.
 *
 * 데스크탑 전용 크롬(Astryx SideNav/TopNav)을 **렌더 자체에서** 분기하기 위해 쓴다.
 * CSS 로 숨기면 Astryx 내부 DOM 구조에 의존해야 하는데, 그건 버전이 바뀌면 깨진다.
 *
 * ⚠ 브레이크포인트를 넘나들 때 재렌더는 되지만 children 은 같은 엘리먼트 참조라
 * 언마운트되지 않는다 — 메인 탭 keep-alive 슬롯의 상태가 보존된다.
 */
import { useEffect, useState } from "react";

import { isNative } from "../native";

export const DESKTOP_QUERY = "(min-width: 1024px)";

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/**
 * peeka 데스크탑 웹 기준 — Layout·페이지 CSS 의 @media 와 같은 1024px.
 *
 * ⚠ **네이티브는 폭과 무관하게 항상 false.** iPad 13인치 세로가 정확히 1024pt 라
 * 폭만 보면 태블릿 앱이 데스크탑 웹 크롬(SideNav/TopNav)으로 떠 버린다. 앱은
 * 화면이 넓어도 하단탭 레이아웃을 쓴다 (사용자 결정 2026-09-15, iPad 지원 추가).
 * 같은 게이트가 CSS 쪽에도 있다 — global.css·Layout.module.css·ScanPage.module.css
 * 의 1024px 블록은 `html[data-platform="web"]` 스코프다. 한쪽만 고치면 iPad 에서
 * 하단탭이 숨겨진 채 데스크탑 크롬도 없는 상태가 된다.
 */
export function useIsDesktop(): boolean {
  const wide = useMediaQuery(DESKTOP_QUERY);
  return wide && !isNative();
}
